import React from 'react';
import { Label } from '@jem-open/jem-ui';
import Select from 'react-select';
import { useMultiProductRecon } from '../MultiProductReconContext';
import { PayCycle } from '../CreateMultiProductReconModal';

interface PayCycleSelectorProps {
  payCycles: PayCycle[];
}

// Options carry the frequency so a selection made under one type filter keeps
// its frequency once the filter moves on.
interface PayCycleOption {
  value: number;
  label: string;
  frequency: string;
}

const toOption = (pC: PayCycle): PayCycleOption => ({
  value: pC.id,
  label: `${pC.name}  |  ${pC.frequency} | ${pC.frequency_type}`,
  frequency: pC.frequency,
});

const PayCycleSelector = ({ payCycles }: PayCycleSelectorProps) => {
  const { state, setPayCycleFrequencyFilter, setEmployerPayCycles } =
    useMultiProductRecon();

  const frequencyOptions = Array.from(
    new Set(payCycles.map((pC) => pC.frequency)),
  ).map((freq) => ({ value: freq, label: freq }));

  // No filter means every pay cycle is offered. Already-selected cycles stay
  // visible as chips even while the filter hides them from the options list.
  const payCycleOptions = payCycles
    .filter(
      (pC) =>
        !state.payCycleFrequencyFilter ||
        pC.frequency === state.payCycleFrequencyFilter,
    )
    .map(toOption);

  const selectedIds = state.employerPayCycles.map((pC) => pC.id);
  const selectedPayCycleOptions = payCycles
    .filter((pC) => selectedIds.includes(pC.id))
    .map(toOption);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor="mp-pay-cycle-type">Pay Cycle Type</Label>
        <Select
          inputId="mp-pay-cycle-type"
          isClearable
          options={frequencyOptions}
          value={
            state.payCycleFrequencyFilter
              ? {
                  value: state.payCycleFrequencyFilter,
                  label: state.payCycleFrequencyFilter,
                }
              : null
          }
          onChange={(opt) =>
            setPayCycleFrequencyFilter(opt ? opt.value : null)
          }
          placeholder="All pay cycle types"
        />
        <p className="text-xs text-muted-foreground">
          Optional filter for the list below. Changing it keeps the pay cycles
          you have already selected, so one recon can cover several types.
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="mp-pay-cycles">Pay Cycles</Label>
        <Select
          inputId="mp-pay-cycles"
          isMulti
          options={payCycleOptions}
          value={selectedPayCycleOptions}
          onChange={(selected) =>
            setEmployerPayCycles(
              selected
                ? selected.map((s) => ({ id: s.value, frequency: s.frequency }))
                : [],
            )
          }
          placeholder="Select pay cycles..."
        />
      </div>
    </div>
  );
};

export default PayCycleSelector;
